import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './table.css';
import { BasicTrackingFn, EventElementType, SortDirection, SortingInfo, SortOption, SortType } from '../../types';
import { TrackSortTypes, trackTableClicked } from '../../analytics/utils';
import { debounce } from '../../export/utils/debounce';
import classNames from 'classnames';
import { useMobile } from '../../context/mobile/mobile.provider';

const SORT_DIRECTION_OPACITY = '100%';
const SORT_OPACITY = '30%';

function makeBorderCss(borders: string): string {
  let bordersCss = '';
  if (borders.includes('b')) {
    bordersCss += ' border-b';
  }
  if (borders.includes('l')) {
    bordersCss += ' border-l';
  }
  if (borders.includes('r')) {
    bordersCss += ' border-r';
  }
  if (borders.includes('t')) {
    bordersCss += ' border-t';
  }
  return bordersCss;
}

type TableProps = {
  children: ReactNode;
  bottomScrollPosition?: number;
};
function Table({ children, bottomScrollPosition }: TableProps) {
  const tableRef = useRef<HTMLTableElement>(null);
  const [tableEl, setTableEl] = useState<HTMLTableElement>();
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const { isMobile } = useMobile();

  const debouncedSetScrollbarWidth = useCallback(
    debounce(tableEl => {
      if (tableEl) {
        setScrollbarWidth(tableEl.offsetWidth);
      }
    }, 100),
    []
  );

  useEffect(() => {
    if (!tableEl) return;

    const observer = new ResizeObserver(() => debouncedSetScrollbarWidth(tableEl));
    observer.observe(tableEl);

    return () => observer.disconnect();
  }, [tableEl]);

  useEffect(() => {
    if (tableRef.current) {
      setTableEl(tableRef.current);
    }
  }, [tableRef.current]);

  return (
    <div style={{ minHeight: 480 }} className="relative">
      <div style={{ minHeight: 'inherit' }} className="table-container overflow-x-auto">
        <div style={isMobile ? { height: '480px' } : {}}>
          <table ref={tableRef} className="relative w-full text-sm" style={{ tableLayout: 'auto' }} role="grid">
            {children}
          </table>
        </div>
      </div>
      <TableScrollbar tableEl={tableEl} width={scrollbarWidth} bottomScrollPosition={bottomScrollPosition} />
    </div>
  );
}

type HeaderProps = {
  children: ReactNode;
  withRows?: boolean;
  isStickyTop?: boolean;
};
function TableHeader({ children, withRows = true, isStickyTop }: HeaderProps) {
  return (
    <thead className={isStickyTop ? 'sticky-header' : ''}>
      {withRows && <tr>{children}</tr>}
      {!withRows && <>{children}</>}
    </thead>
  );
}

type ColumnProps = {
  children: ReactNode;
  align?: 'left' | 'right' | 'center';
  colSpan?: number;
  rowSpan?: number;
  showHover?: boolean;
  borders?: string;
  minWidth?: boolean;
  width?: string;
  isSorted?: boolean;
  isStickyLeft?: boolean;
};
function TableColumn({
  children,
  align = 'left',
  borders = 'b',
  minWidth = false,
  colSpan,
  rowSpan,
  isSorted = false,
  showHover = false,
  width,
  isStickyLeft,
}: ColumnProps) {
  const { isMobile } = useMobile();

  return (
    // the class left-38 will be applied only in leaderboards where 2 columns are sticky and the second one has sorting enabled
    <th
      className={classNames(
        `top-0 border-light ${showHover && 'hover:bg-dark'} ${makeBorderCss(borders)} ${minWidth ? 'w-1' : ''} ${
          isSorted ? 'bg-dark' : 'bg-medium'
        } ${width || ''} ${isStickyLeft ? 'sticky left-0 z-40 w-24' : ''}`,
        {
          'left-38': isStickyLeft && showHover,
          'p-2': !isMobile,
          'pt-1': isMobile,
          'px-2': isMobile && isStickyLeft,
          'px-1': isMobile && !isStickyLeft,
        }
      )}
      colSpan={colSpan || 1}
      rowSpan={rowSpan || 1}
      role="columnheader"
    >
      <span
        className={`flex-column ${align === 'left' ? 'items-start' : align === 'right' ? 'items-end' : 'items-center'}`}
      >
        {children}
      </span>
    </th>
  );
}

type BodyProps = {
  children: ReactNode;
};
function TableBody({ children }: BodyProps) {
  return <tbody className="bg-white">{children}</tbody>;
}

type RowProps = {
  children: ReactNode;
  important?: boolean;
  border?: string;
  bgColor?: string;
};
function TableRow({ children, important = false, border, bgColor }: RowProps) {
  return (
    <tr className={`relative ${important ? ' font-bold bg-light' : ''} ${border || ''} ${bgColor || ''}`} role="row">
      {children}
    </tr>
  );
}

type CellProps = {
  children: ReactNode;
  align?: 'left' | 'right' | 'center';
  borders?: string;
  colSpan?: number;
  rowSpan?: number;
  selected?: boolean;
  important?: boolean;
  isSorted?: boolean;
  isStickyLeft?: boolean;
  onClick?: (e) => void;
};
function TableCell({
  children,
  borders = 'b',
  align = 'right',
  colSpan,
  rowSpan,
  selected,
  onClick,
  isSorted,
  important,
  isStickyLeft,
}: CellProps) {
  const { isMobile } = useMobile();

  const handleClick = e => {
    if (onClick) {
      onClick(e);
    }
  };
  const tdCustomClass = classNames({
    'bg-brand-lightest': selected,
    'bg-light': isSorted || important,
    'bg-medium': isSorted && important,
    'bg-white': !important,
    'cursor-pointer': onClick,
    'sticky left-0 z-30 w-24': isStickyLeft,
    'left-38': isStickyLeft && onClick,
    'p-2': !isMobile,
    'pt-1': isMobile,
    'px-2': isMobile && isStickyLeft,
    'px-1': isMobile && !isStickyLeft,
  });
  return (
    <td
      className={`relative border-light ${makeBorderCss(borders)} ${tdCustomClass}`}
      valign="middle"
      align={align}
      colSpan={colSpan || 1}
      rowSpan={rowSpan || 1}
      onClick={handleClick}
      role="gridcell"
    >
      <span
        className={`flex-column ${align === 'left' ? 'items-start' : align === 'right' ? 'items-end' : 'items-center'}`}
      >
        {children}
      </span>
    </td>
  );
}

type TableScrollbarProps = {
  tableEl: HTMLTableElement;
  width: number;
  bottomScrollPosition?: number;
};
function TableScrollbar({ tableEl, bottomScrollPosition = 0, width }: TableScrollbarProps) {
  const innerScrollerRef = useRef(null);

  const innerScrollEl = innerScrollerRef.current;

  useLayoutEffect(() => {
    if (!tableEl || !innerScrollEl) return;

    const containerOnScroll = e => {
      innerScrollEl.parentElement.scrollLeft = e.target.scrollLeft;
    };

    const innerScrollOnScroll = e => {
      tableEl.parentElement.parentElement.scrollLeft = e.target.scrollLeft;
    };

    tableEl.parentElement.parentElement.addEventListener('scroll', containerOnScroll);
    innerScrollEl.parentElement.addEventListener('scroll', innerScrollOnScroll);

    return () => {
      tableEl.parentElement.removeEventListener('scroll', containerOnScroll);
      innerScrollEl.parentElement.removeEventListener('scroll', innerScrollOnScroll);
    };
  }, [tableEl?.offsetWidth]);

  return (
    <div className="sticky h-4 bg-transparent overflow-x-auto " style={{ bottom: `${bottomScrollPosition}px` }}>
      <div ref={innerScrollerRef} style={{ height: '1px', width: width }} />
    </div>
  );
}

type SortingProps = {
  children: ReactNode;
  colId: string[];
  colLabel: string;
  options: SortOption[];
  value: SortingInfo;
  columnIndex?: number;
  onSort: (sorting: SortingInfo) => void;
  trackEvent?: BasicTrackingFn;
  position?: 'left' | 'right';
};
function Sorting({
  children,
  colId,
  colLabel,
  value,
  options,
  columnIndex = 0,
  position = 'left',
  onSort,
  trackEvent,
}: SortingProps) {
  const [showOptions, setShowOptions] = useState(false);
  const wrapperRef = useRef(null);

  const paddingOnItems = 'py-2 px-3';
  const labelCss = 'block text-default';
  const subLabelCss = 'block text-lighter';
  const buttonCss = `flex-grow block text-left ${paddingOnItems} hover:bg-light`;

  const onSortClick = (option: SortingInfo) => {
    trackTableClicked(trackEvent, TrackSortTypes.sort, EventElementType.Sort, {
      column: colLabel,
      optionChosen: `${option.sortType} ${option.direction}`,
    });
    onSort(option);
    setShowOptions(false);
  };

  const arrowOpacity = (sortDirection: SortDirection) => {
    return (
      colId.includes(value?.sortKey) &&
      value?.sortIndex === columnIndex &&
      value?.direction === sortDirection &&
      SORT_DIRECTION_OPACITY
    );
  };

  const getLabelForSortOption = (type: SortType, direction: SortDirection): string => {
    if (type === SortType.Alpha) {
      return direction === SortDirection.ascending ? 'Ascending (A→Z)' : 'Descending (Z→A)';
    } else {
      return direction === SortDirection.ascending ? 'Ascending (0→9)' : 'Descending (9→0)';
    }
  };

  const onClickHeader = () => {
    if (options.length > 1) {
      setShowOptions(true);
      return;
    }
    let newDirection: SortDirection =
      options[0].sortType === SortType.Alpha ? SortDirection.ascending : SortDirection.descending;
    if (value.sortKey === options[0].sortKey) {
      newDirection = value.direction === SortDirection.ascending ? SortDirection.descending : SortDirection.ascending;
    }
    onSortClick({
      columnName: colId.join('|'),
      sortKey: options[0].sortKey,
      sortType: options[0].sortType,
      label: options[0].label,
      direction: newDirection,
      sortIndex: columnIndex,
    });
  };
  const upArrowOpacity = arrowOpacity(SortDirection.ascending);
  const downArrowOpacity = arrowOpacity(SortDirection.descending);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };

    document.addEventListener('click', handleClickOutside, false);
    return () => {
      document.removeEventListener('click', handleClickOutside, false);
    };
  }, []);

  return (
    <div
      role="presentation"
      data-test-id={`sorting-header-${colId.join('-')}`}
      ref={wrapperRef}
      className="relative w-full flex justify-between cursor-pointer"
      onClick={onClickHeader}
    >
      <span>{children}</span>
      <svg role="img" width={24} height={24} xmlns="http://www.w3.org/2000/svg" strokeWidth="0" viewBox="0 0 24 24">
        <path d="M7 14L12 20L17 14H7Z" fill="#5C626E" fillOpacity={upArrowOpacity || SORT_OPACITY} />
        <path d="M17 10L12 4L7 10L17 10Z" fill="#5C626E" fillOpacity={downArrowOpacity || SORT_OPACITY} />
      </svg>
      {showOptions && (
        <div
          role="presentation"
          data-test-id="sort-options"
          style={{ top: 38 }}
          className={`absolute text-left font-normal z-20 border border-default rounded w-60 bg-white ${position}-0 cursor-default shadow-lg`}
          onMouseLeave={() => setShowOptions(false)}
          onClick={e => e.stopPropagation()}
        >
          <div className={`font-bold text-default ${paddingOnItems}`}>Sort by</div>
          <div className="pb-2">
            {options.map((option: SortOption, idx) => {
              const commonSortOptions = {
                columnName: colId.join('|'),
                sortKey: option.sortKey,
                sortType: option.sortType,
                label: option.label,
              };
              return (
                <div key={option.sortKey} className="flex flex-column">
                  <button
                    data-test-id={`${option.sortKey}-desc`}
                    className={buttonCss}
                    onClick={() => {
                      onSortClick({
                        ...commonSortOptions,
                        direction: SortDirection.descending,
                        sortIndex: columnIndex,
                      });
                    }}
                  >
                    <span className={labelCss}>{option.label}</span>
                    <span className={subLabelCss}>
                      {getLabelForSortOption(option.sortType, SortDirection.descending)}
                    </span>
                  </button>
                  <button
                    data-test-id={`${option.sortKey}-asc`}
                    className={buttonCss}
                    onClick={() => {
                      onSortClick({
                        ...commonSortOptions,
                        direction: SortDirection.ascending,
                        sortIndex: columnIndex,
                      });
                    }}
                  >
                    <span className={labelCss}>{option.label}</span>
                    <span className={subLabelCss}>
                      {getLabelForSortOption(option.sortType, SortDirection.ascending)}
                    </span>
                  </button>
                  {/** Hides the divider if last options **/}
                  {idx !== options.length - 1 && <hr className={`m-0 border-light mx-3`} />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

Table.Header = TableHeader;
Table.Column = TableColumn;
Table.Body = TableBody;
Table.Row = TableRow;
Table.Cell = TableCell;
Table.Scrollbar = TableScrollbar;
Table.Sorting = Sorting;

export { Table };
